import React, { lazy, useEffect } from 'react';

import { Navigate, useRoutes } from 'react-router';

import { AbilityContextProvider } from '@/contexts/ability';
import { useUserContext } from '@/contexts/user';
import { SettingsContextProvider } from '@/contexts/settings';
import { WorkspaceContextProvider } from '@/contexts/workspace';
import { NotificationsContextProvider } from '@/contexts/notifications';
import { SystemNotificationsProvider } from '@/contexts/systemNotifications';
import { TaskAccessProvider } from '@/modern/widgets/lib/TaskAccessProvider';

import {
  APP_ROUTES,
} from '@/assets/MenuItems';

import { PUBLIC_ROUTES } from '@/views/public/routes';
import { NotFoundPage } from '@/modern/shell';
import { WithOwnershipProvider } from '@/views/common/WithOwnership/context';

import { AuthenticatedLayout } from '@/layouts/Authenticated';
import { NotAuthenticatedLayout } from '@/layouts/NotAuthenticated';
import { WorkspaceIdRedirect } from '@/views/WorkspaceIdRedirect';
import { persistNavChromeFromParam } from '@/config/navChrome';
import { NavChromeProvider, useNavChrome } from '@/layouts/nav/NavChromeContext';

const loadAppLayout = () => import('@/layouts/AppLayout');
const AppLayout = lazy(loadAppLayout);

function AppRoutes() {
  const { chrome: navChrome } = useNavChrome();
  const { userIsLogged } = useUserContext();
  const isLogged = userIsLogged();

  useEffect(() => {
    persistNavChromeFromParam();
  }, []);

  useEffect(() => {
    if (isLogged) loadAppLayout();
  }, [isLogged]);

  const layoutChrome = navChrome === 'top' ? 'topbar' : 'sidebar';

  return useRoutes([
    {
      element: <NotAuthenticatedLayout />,
      children: [
        ...PUBLIC_ROUTES,
      ],
    },
    {
      element: (
        <WorkspaceContextProvider>
          <TaskAccessProvider>
            <AbilityContextProvider>
              <WithOwnershipProvider>
                <SettingsContextProvider>
                  <NotificationsContextProvider>
                    <SystemNotificationsProvider>
                      <AuthenticatedLayout />
                    </SystemNotificationsProvider>
                  </NotificationsContextProvider>
                </SettingsContextProvider>
              </WithOwnershipProvider>
            </AbilityContextProvider>
          </TaskAccessProvider>
        </WorkspaceContextProvider>
      ),
      children: [
        {
          path: '/ws/:workspaceId/*',
          element: <WorkspaceIdRedirect />,
        },
        {
          path: '/:workspace',
          element: <AppLayout chrome={layoutChrome} />,
          children: [...APP_ROUTES],
        },
        {
          path: '/',
          element: <AppLayout chrome={layoutChrome} />,
        },
      ],
    },
    {
      path: '/404',
      element: <NotFoundPage />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);
}

function Routes() {
  return (
    <NavChromeProvider>
      <AppRoutes />
    </NavChromeProvider>
  );
}

export default Routes;
